import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useExecutions } from '../hooks/useExecutions'
import { ExecutionStatus } from '../components/ExecutionStatus'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorAlert } from '../components/ErrorAlert'

export function ReportHistory() {
  const { id } = useParams()
  const { loading, error, getReportExecutions } = useExecutions()
  const [executions, setExecutions] = useState([])

  useEffect(() => {
    loadExecutions()
  }, [id])

  async function loadExecutions() {
    try {
      const data = await getReportExecutions(id)
      setExecutions(data.executions || [])
    } catch (err) {
      console.error('Failed to load executions:', err)
    }
  }

  function formatDate(isoString) {
    const date = new Date(isoString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  function formatDuration(ms) {
    if (ms < 1000) return `${ms}ms`
    const seconds = (ms / 1000).toFixed(1)
    return `${seconds}s`
  }

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-2 inline-block">
          ← Back to Reports
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Execution History</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
          View past executions of this report
        </p>
      </div>

      {error && (
        <ErrorAlert message={error} />
      )}

      {loading ? (
        <LoadingSpinner message="Loading executions..." />
      ) : executions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No executions yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            This report hasn't been executed yet. It will run on its scheduled days.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {executions.map((execution) => {
              const linkPath = `/executions/${id}/${encodeURIComponent(execution.execution_time)}`
              return (
                <div key={execution.execution_time} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {formatDate(execution.execution_time)}
                      </div>
                      <ExecutionStatus status={execution.status} />
                    </div>
                    <Link
                      to={linkPath}
                      className="ml-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      View
                    </Link>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Duration</div>
                      <div className="text-gray-900 font-medium">
                        {execution.duration_ms ? formatDuration(execution.duration_ms) : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Email</div>
                      <div>
                        {execution.email_sent ? (
                          <span className="text-green-600 font-medium">✓ Sent</span>
                        ) : (
                          <span className="text-gray-400 font-medium">✗ Not sent</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Audio</div>
                      <div>
                        {execution.audio_url ? (
                          <span className="text-blue-600 font-medium">🎧 Yes</span>
                        ) : (
                          <span className="text-gray-400 font-medium">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Audio
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {executions.map((execution) => {
                  const linkPath = `/executions/${id}/${encodeURIComponent(execution.execution_time)}`
                  return (
                    <tr key={execution.execution_time} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(execution.execution_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ExecutionStatus status={execution.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {execution.duration_ms ? formatDuration(execution.duration_ms) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {execution.email_sent ? (
                          <span className="text-green-600">✓ Sent</span>
                        ) : (
                          <span className="text-gray-400">✗ Not sent</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {execution.audio_url ? (
                          <span className="text-blue-600">🎧 Available</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={linkPath}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
